import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Paper,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { ProcedureToAdd, SoapProcedure } from '../../types/soap';

interface ProcedureConfirmationDialogProps {
  open: boolean;
  procedures: SoapProcedure[];
  noteDate: Date | string;
  onConfirm: (selectedProcedures: ProcedureToAdd[]) => void;
  onCancel: () => void;
}

interface EditableProcedure extends ProcedureToAdd {
  selected: boolean;
  originalIndex: number;
}

const ProcedureConfirmationDialog: React.FC<ProcedureConfirmationDialogProps> = ({
  open,
  procedures,
  noteDate,
  onConfirm,
  onCancel,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [editableProcedures, setEditableProcedures] = useState<EditableProcedure[]>([]);

  // Initialize editable procedures when dialog opens
  useEffect(() => {
    if (open && procedures.length > 0) {
      const initialized = procedures.map((proc, index) => {
        return {
          selected: true, // All selected by default
          originalIndex: index,
          name: proc.name,
          notes: proc.notes || '',
          date: new Date(noteDate).toISOString(),
        };
      });
      setEditableProcedures(initialized);
    }
  }, [open, procedures, noteDate]);

  const handleToggleSelection = (index: number) => {
    setEditableProcedures(prev =>
      prev.map((proc, i) =>
        i === index ? { ...proc, selected: !proc.selected } : proc
      )
    );
  };

  const handleAddProcedure = () => {
    const newProcedure: EditableProcedure = {
      selected: true,
      originalIndex: editableProcedures.length,
      name: '',
      notes: '',
      date: new Date(noteDate).toISOString(),
    };
    setEditableProcedures(prev => [...prev, newProcedure]);
  };

  const handleDeleteProcedure = (index: number) => {
    setEditableProcedures(prev => prev.filter((_, i) => i !== index));
  };

  const handleFieldChange = (index: number, field: keyof EditableProcedure, value: string) => {
    setEditableProcedures(prev =>
      prev.map((proc, i) => {
        if (i !== index) return proc;
        return { ...proc, [field]: value };
      })
    );
  };

  const handleConfirm = () => {
    const selectedProcs = editableProcedures
      .filter(proc => proc.selected && proc.name.trim())
      .map(({ selected, originalIndex, ...proc }) => proc);
    onConfirm(selectedProcs);
  };

  const selectedCount = editableProcedures.filter(proc => proc.selected && proc.name.trim()).length;

  return (
    <Dialog 
      open={open} 
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { 
          minHeight: { xs: '100%', sm: '60vh' }, 
          maxHeight: { xs: '100%', sm: '90vh' },
          m: { xs: 0, sm: 2 }
        }
      }}
    >
      <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexDirection={{ xs: 'column', sm: 'row' }} gap={1}>
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Confirm Procedures to Add
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            {selectedCount} of {editableProcedures.length} selected
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2 } }}>
        <Box mb={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            Review and edit procedures before adding them to the record. 
            Uncheck procedures you don't want to add, or adjust details as needed.
            {!isMobile && ' The procedure will be created as a placeholder - you\'ll need to go to the Procedures page to complete the full details.'}
          </Typography>
          <Stack direction="row" spacing={1} mt={1}>
            <Button
              size={isMobile ? "small" : "medium"}
              startIcon={!isMobile && <AddIcon />}
              onClick={handleAddProcedure}
              variant="contained"
              color="primary"
              fullWidth={isMobile}
              sx={{ minHeight: 44, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            >
              {isMobile ? 'Add' : 'Add Procedure'}
            </Button>
          </Stack>
        </Box>

        {isMobile || isTablet ? (
          // Card layout for mobile/tablet
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {editableProcedures.map((proc, index) => (
              <Card 
                key={index} 
                variant="outlined"
                sx={{ 
                  bgcolor: proc.selected ? 'transparent' : 'action.disabledBackground',
                  position: 'relative'
                }}
              >
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Checkbox
                        checked={proc.selected}
                        onChange={() => handleToggleSelection(index)}
                        size="small"
                      />
                      <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>
                        Procedure {index + 1}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteProcedure(index)}
                      color="error"
                      sx={{ mt: -0.5, mr: -0.5 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Stack spacing={1.5}>
                    <TextField
                      label="Procedure Name"
                      value={proc.name}
                      onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                      size="small"
                      fullWidth
                      disabled={!proc.selected}
                      placeholder="e.g., Ultrasound"
                    />
                    <TextField
                      label="Notes"
                      value={proc.notes}
                      onChange={(e) => handleFieldChange(index, 'notes', e.target.value)}
                      size="small"
                      fullWidth
                      disabled={!proc.selected}
                      multiline
                      rows={3}
                      placeholder="Additional details..."
                    />
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          // Table layout for desktop
          <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell padding="checkbox" width={50}>
                  Select
                </TableCell>
                <TableCell width={200}>Procedure Name</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell width={20}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {editableProcedures.map((proc, index) => (
                <TableRow 
                  key={index}
                  sx={{ 
                    bgcolor: proc.selected ? 'transparent' : 'action.disabledBackground',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={proc.selected}
                      onChange={() => handleToggleSelection(index)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={proc.name}
                      onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                      size="small"
                      fullWidth
                      disabled={!proc.selected}
                      placeholder="e.g., Ultrasound"
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={proc.notes}
                      onChange={(e) => handleFieldChange(index, 'notes', e.target.value)}
                      size="small"
                      fullWidth
                      disabled={!proc.selected}
                      multiline
                      maxRows={3}
                      placeholder="Additional details..."
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Delete procedure">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteProcedure(index)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}

        {selectedCount === 0 && (
          <Box mt={2}>
            <Typography variant="body2" color="warning.main" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
              No procedures selected. Click "Confirm & Complete" to complete the SOAP note without adding procedures.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, pt: 1, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
        <Button 
          onClick={onCancel} 
          startIcon={!isMobile && <CancelIcon />}
          fullWidth={isMobile}
          sx={{ minHeight: 44, order: { xs: 2, sm: 1 } }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          startIcon={!isMobile && <CheckCircleIcon />}
          fullWidth={isMobile}
          sx={{ minHeight: 44, order: { xs: 1, sm: 2 } }}
        >
          {isMobile ? 'Confirm' : 'Confirm & Complete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProcedureConfirmationDialog;

